import { createBrowserRouter, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { type ReactNode, useEffect, useState, lazy, Suspense } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Spinner } from './components/Spinner';
import { ErrorBoundary } from './components/ErrorBoundary';

interface ProtectedRouteProps {
  children?: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps): JSX.Element => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?redirect=${redirect}`} replace />;
  }

  return <>{children ?? <Outlet />}</>;
};

// Landing-page guard. Behaves differently from GuestRoute in two ways:
//
//   1. Renders children IMMEDIATELY without waiting for the auth check.
//      Anonymous visitors (the dominant traffic on the landing page)
//      should never see a dark full-screen spinner flash before the
//      marketing copy paints. The auth check resolves in a render or
//      two; only the rare logged-in visitor briefly sees the landing
//      before the Navigate kicks in.
//
//   2. Does NOT honor a `?redirect=` query param. The landing route is
//      the marketing entry point, not a sign-in redirect target — any
//      `?redirect=` here would be junk a marketing link picked up, and
//      we shouldn't act on it.
//
// Why this exists: a logged-in user hitting `/` previously saw the
// landing page (with its "Start free trial" / "Sign in" CTAs) and got
// confused about whether they needed to log in again.
interface LandingRouteProps {
  children?: ReactNode;
}

export const LandingRoute = ({ children }: LandingRouteProps): JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  if (isAuthenticated === true) {
    return <Navigate to="/dashboard" replace />;
  }

  // Both `null` (check in flight) and `false` (anonymous) render the
  // landing eagerly. The null path is the load-time race the comment
  // above describes — accepted to avoid the spinner flash.
  return <>{children ?? <Outlet />}</>;
};

interface GuestRouteProps {
  children?: ReactNode;
}

export const GuestRoute = ({ children }: GuestRouteProps): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Honor the original destination if the user was mid-flow.
    // URLSearchParams.get() already decodes, so no second decodeURIComponent.
    const redirect = searchParams.get('redirect') ?? '/dashboard';
    return <Navigate to={redirect} replace />;
  }

  return <>{children ?? <Outlet />}</>;
};

// Lazy imports
const LazyDashboard = lazy(() => import('./features/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const LazyPropertyDetail = lazy(() => import('./features/dashboard/PropertyDetailPage').then(m => ({ default: m.PropertyDetailPage })));
const LazyRentalDetail = lazy(() => import('./features/dashboard/RentalDetailPage').then(m => ({ default: m.RentalDetailPage })));
const LazyReports = lazy(() => import('./features/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const LazyReportDetail = lazy(() => import('./features/reports/ReportDetailPage').then(m => ({ default: m.ReportDetailPage })));
const LazyNLQuery = lazy(() => import('./features/nl-query/NLQueryPage').then(m => ({ default: m.NLQueryPage })));
const LazyMonitors = lazy(() => import('./features/zip-monitors/MonitorsPage').then(m => ({ default: m.MonitorsPage })));
const LazyWatchlist = lazy(() => import('./features/watchlist/WatchlistPage').then(m => ({ default: m.WatchlistPage })));
const LazyUnsubscribe = lazy(() => import('./features/settings/UnsubscribePage').then(m => ({ default: m.UnsubscribePage })));
const LazyReportShare = lazy(() => import('./features/reports/ReportSharePage').then(m => ({ default: m.ReportSharePage })));
const LazyMarketSnapshot = lazy(() => import('./features/markets/MarketSnapshotPage').then(m => ({ default: m.MarketSnapshotPage })));
const LazySettings = lazy(() => import('./features/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const LazySignIn = lazy(() => import('./features/auth/SignInPage').then(m => ({ default: m.SignInPage })));
const LazySignUp = lazy(() => import('./features/auth/SignUpPage').then(m => ({ default: m.SignUpPage })));
const LazyConfirm = lazy(() => import('./features/auth/ConfirmPage').then(m => ({ default: m.ConfirmPage })));
const LazyForgotPassword = lazy(() => import('./features/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const LazyResetPassword = lazy(() => import('./features/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const LazyAppLayout = lazy(() => import('./components/AppLayout').then(m => ({ default: m.AppLayout })));
const LazyLanding = lazy(() => import('./features/landing/LandingPage').then(m => ({ default: m.LandingPage })));
const LazyNotFound = lazy(() => import('./features/errors/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const LazyErrorPage = lazy(() => import('./features/errors/ErrorPage').then(m => ({ default: m.ErrorPage })));

const PageSuspense = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<div className="flex h-screen items-center justify-center bg-slate-900"><Spinner size="lg" /></div>}>
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  { path: '/', element: <LandingRoute><PageSuspense><LazyLanding /></PageSuspense></LandingRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  {
    errorElement: <PageSuspense><LazyErrorPage /></PageSuspense>,
    element: (
      <ProtectedRoute>
        <PageSuspense>
          <LazyAppLayout />
        </PageSuspense>
      </ProtectedRoute>
    ),
    children: [
      { path: '/dashboard', element: <ErrorBoundary><PageSuspense><LazyDashboard /></PageSuspense></ErrorBoundary> },
      { path: '/dashboard/:zipCode/properties/:addressSlug', element: <ErrorBoundary><PageSuspense><LazyPropertyDetail /></PageSuspense></ErrorBoundary> },
      { path: '/dashboard/:zipCode/rentals/:addressSlug', element: <ErrorBoundary><PageSuspense><LazyRentalDetail /></PageSuspense></ErrorBoundary> },
      { path: '/reports', element: <ErrorBoundary><PageSuspense><LazyReports /></PageSuspense></ErrorBoundary> },
      { path: '/reports/:reportId', element: <ErrorBoundary><PageSuspense><LazyReportDetail /></PageSuspense></ErrorBoundary> },
      { path: '/query', element: <ErrorBoundary><PageSuspense><LazyNLQuery /></PageSuspense></ErrorBoundary> },
      { path: '/monitors', element: <ErrorBoundary><PageSuspense><LazyMonitors /></PageSuspense></ErrorBoundary> },
      { path: '/watchlist', element: <ErrorBoundary><PageSuspense><LazyWatchlist /></PageSuspense></ErrorBoundary> },
      { path: '/settings', element: <ErrorBoundary><PageSuspense><LazySettings /></PageSuspense></ErrorBoundary> },
    ],
  },
  { path: '/markets/:zip', element: <PageSuspense><LazyMarketSnapshot /></PageSuspense>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/r/:token', element: <PageSuspense><LazyReportShare /></PageSuspense>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/unsubscribe/:token', element: <PageSuspense><LazyUnsubscribe /></PageSuspense>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/sign-in', element: <GuestRoute><PageSuspense><LazySignIn /></PageSuspense></GuestRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/sign-up', element: <GuestRoute><PageSuspense><LazySignUp /></PageSuspense></GuestRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/confirm', element: <GuestRoute><PageSuspense><LazyConfirm /></PageSuspense></GuestRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/forgot-password', element: <GuestRoute><PageSuspense><LazyForgotPassword /></PageSuspense></GuestRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '/reset-password', element: <GuestRoute><PageSuspense><LazyResetPassword /></PageSuspense></GuestRoute>, errorElement: <PageSuspense><LazyErrorPage /></PageSuspense> },
  { path: '*', element: <PageSuspense><LazyNotFound /></PageSuspense> },
]);
